import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { useMemo, useState } from 'react'
import { scheduleTermChanges, updateTermChange } from 'services'
import { Modal } from 'stories/components'
import { formatDate } from 'utils'

import { type TermChangeRow, FIELD_LABELS } from './types'

interface Step {
  effectiveDate: string
  value: string
}

/** Rate, P&I and impound all moving on one date, entered together. */
const MULTI = 'multi'

const unitOf = (field: string) => (field === 'rate' ? '%' : '$')
const placeholderOf = (field: string) => (field === 'rate' ? '9.500' : '1,250.00')
const stepOf = (field: string) => (field === 'rate' ? '0.001' : '0.01')

/**
 * Schedule a change to the loan's rate, P&I or escrow.
 *
 * Three shapes of the same form: one field on one date, one field on several dates (a step-rate
 * loan is a list of dates and rates entered together), or several fields on one date (a note
 * modification that moves the rate, the payment and the impound at once). Editing an existing
 * scheduled change reuses it with a single row.
 *
 * Nothing stops two changes to one field sharing a date - the later entry simply wins - but that
 * is almost always a correction meant for the change already there, so the form says so.
 */
export const ScheduleChangeModal = ({
  editing,
  allowedFields,
  existing,
  onClose,
}: {
  editing: TermChangeRow | null
  allowedFields: string[]
  /** The loan's changes already on file, to warn about a date that is taken. */
  existing: TermChangeRow[]
  onClose: (changed: boolean) => void
}) => {
  const [field, setField] = useState(editing?.field || allowedFields[0] || 'rate')
  const [steps, setSteps] = useState<Step[]>([
    { effectiveDate: editing?.effectiveDate || '', value: editing ? String(editing.value) : '' },
  ])
  const [multiDate, setMultiDate] = useState('')
  const [multiValues, setMultiValues] = useState<Record<string, string>>({})
  const [note, setNote] = useState(editing?.note || '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const isMulti = field === MULTI
  const setStep = (index: number, patch: Partial<Step>) =>
    setSteps((prev) => prev.map((step, i) => (i === index ? { ...step, ...patch } : step)))

  /** What the form is about to save, as field/date pairs. */
  const pending = useMemo(() => {
    if (isMulti) {
      return allowedFields
        .filter((key) => `${multiValues[key] ?? ''}`.trim() !== '')
        .map((key) => ({ field: key, effectiveDate: multiDate, value: `${multiValues[key]}`.trim() }))
    }
    return steps
      .filter((step) => step.effectiveDate || step.value.trim() !== '')
      .map((step) => ({ field, effectiveDate: step.effectiveDate, value: step.value.trim() }))
  }, [isMulti, allowedFields, multiValues, multiDate, steps, field])

  /** Dates already carrying a change to the same field, plus any repeated within the form. */
  const conflicts = useMemo(() => {
    const found: string[] = []
    const seen = new Set<string>()
    for (const row of pending) {
      if (!row.effectiveDate) continue
      const key = `${row.field}|${row.effectiveDate}`
      const taken = (existing || []).some(
        (other) =>
          other.field === row.field &&
          other.effectiveDate === row.effectiveDate &&
          other.status !== 'cancelled' &&
          other.id !== editing?.id,
      )
      if (taken || seen.has(key)) found.push(`${FIELD_LABELS[row.field]} on ${formatDate(row.effectiveDate)}`)
      seen.add(key)
    }
    return Array.from(new Set(found))
  }, [pending, existing, editing])

  const onSubmit = async () => {
    setError('')
    if (isMulti) {
      if (!multiDate) {
        setError('Choose the date the changes take effect')
        return
      }
      if (!pending.length) {
        setError('Enter a new value for at least one of them')
        return
      }
    } else if (!pending.length || pending.some((row) => !row.effectiveDate || row.value === '')) {
      setError('Every row needs an effective date and a value')
      return
    }
    setLoading(true)
    try {
      if (editing) {
        await updateTermChange(editing.id, { effectiveDate: pending[0].effectiveDate, value: pending[0].value, note })
      } else {
        // Several rate rows entered together are a step-rate schedule, and are labelled as one.
        const source = field === 'rate' && pending.length > 1 ? 'stepRate' : undefined
        await scheduleTermChanges(pending.map((row) => ({ ...row, note, source })))
      }
      onClose(true)
    } catch (err: any) {
      setError(err?.message || 'Could not save the change')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      title={editing ? 'Edit Scheduled Change' : 'Schedule a Change'}
      titleOkay="Save"
      loading={loading}
      onClose={() => onClose(false)}
      onOk={onSubmit}
      isOpen
    >
      <div className="text-gray-900 w-144 mt-2 mb-3 space-y-4">
        <div>
          <div className="text-[13px] text-gray-500 mb-1">What changes</div>
          <select
            className="text-[14px] border rounded px-2 py-2 w-full"
            value={field}
            disabled={!!editing}
            onChange={(e) => setField(e.target.value)}
          >
            {allowedFields.map((key) => (
              <option key={key} value={key}>
                {FIELD_LABELS[key]}
              </option>
            ))}
            {!editing && allowedFields.length > 1 && <option value={MULTI}>Several, on the same date</option>}
          </select>
          <p className="text-xs text-gray-500 mt-1">
            {isMulti
              ? 'The rate takes effect on its date; the payment and impound take effect with the first payment due on or after it. Leave a value blank to leave that one alone.'
              : field === 'rate'
                ? 'Takes effect on its date. How the billing period it falls in is priced follows the loan’s Rate Change Mid-Period setting on Servicing Information.'
                : 'Takes effect with the first payment due on or after its date.'}
          </p>
        </div>

        {isMulti ? (
          <div className="space-y-3">
            <div>
              <div className="text-[13px] text-gray-500 mb-1">Effective date</div>
              <input
                type="date"
                className="text-[14px] border rounded px-2 py-2 w-1/2"
                value={multiDate}
                onChange={(e) => setMultiDate(e.target.value)}
              />
            </div>
            {allowedFields.map((key) => (
              <div key={key} className="grid grid-cols-[1fr_1fr] gap-2 items-center">
                <span className="text-[13px] text-gray-600">
                  {FIELD_LABELS[key]} ({unitOf(key)})
                </span>
                <input
                  type="number"
                  step={stepOf(key)}
                  className="text-[14px] border rounded px-2 py-2"
                  value={multiValues[key] ?? ''}
                  placeholder={placeholderOf(key)}
                  onChange={(e) => setMultiValues((prev) => ({ ...prev, [key]: e.target.value }))}
                />
              </div>
            ))}
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-[1fr_1fr_24px] gap-2 text-[13px] text-gray-500 mb-1">
              <span>Effective date</span>
              <span>New value ({unitOf(field)})</span>
              <span />
            </div>
            {steps.map((step, index) => (
              <div key={index} className="grid grid-cols-[1fr_1fr_24px] gap-2 mb-2 items-center">
                <input
                  type="date"
                  className="text-[14px] border rounded px-2 py-2"
                  value={step.effectiveDate}
                  onChange={(e) => setStep(index, { effectiveDate: e.target.value })}
                />
                <input
                  type="number"
                  step={stepOf(field)}
                  className="text-[14px] border rounded px-2 py-2"
                  value={step.value}
                  placeholder={placeholderOf(field)}
                  onChange={(e) => setStep(index, { value: e.target.value })}
                />
                {steps.length > 1 ? (
                  <button
                    aria-label="Remove row"
                    className="text-red-800 hover:opacity-70"
                    onClick={() => setSteps((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                ) : (
                  <span />
                )}
              </div>
            ))}
            {!editing && (
              <button
                className="text-sm text-shade-blue hover:underline flex items-center gap-1"
                onClick={() => setSteps((prev) => [...prev, { effectiveDate: '', value: '' }])}
              >
                <PlusIcon className="w-4 h-4" />
                {field === 'rate' ? 'Add a step' : 'Add another date'}
              </button>
            )}
          </div>
        )}

        <div>
          <div className="text-[13px] text-gray-500 mb-1">Note</div>
          <textarea
            className="text-[14px] border rounded px-2 py-2 w-full"
            rows={2}
            value={note}
            placeholder="Why the terms change, e.g. note modification dated 9/1"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
        {conflicts.length > 0 && (
          <div className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            This loan already has a change dated the same day: {conflicts.join(', ')}. Saving adds a second one, and
            the one entered last is the one that applies. To correct the change already there, cancel this and edit it
            instead.
          </div>
        )}
        {error && <div className="text-sm text-red-700">{error}</div>}
      </div>
    </Modal>
  )
}
